import { usePostStore } from "../store/post-store"
import { useUserStore } from "../store/user-store"
import { useFeedStore } from "../store/feed-store"
import {
  fetchCommunityFeed,
  fetchUserPosts,
  fetchUserPostsByType,
} from "../community-api"
import { normalizePostList } from "../normalize"
import { buildFeedKey } from "../utils"
import { dedupFetch } from "./request-dedup"
import type { FeedParams, CommunityFeedResponse } from "../types"

const FEED_SWR = { staleTime: 60_000 } // 1 minute

export async function loadFeed(params: FeedParams, force = false): Promise<void> {
  const feedKey = buildFeedKey(params)

  const { data } = await dedupFetch(
    `feed:${feedKey}:p${params.page ?? 1}`,
    () => {
      if (params.userId && params.abilityType) {
        return fetchUserPostsByType(params.userId, params.abilityType, params.page ?? 1)
      }
      if (params.userId) {
        return fetchUserPosts(params.userId, params.page ?? 1)
      }
      return fetchCommunityFeed(params)
    },
    {
      ...FEED_SWR,
      force,
      onStaleRevalidate: (fresh) => {
        if (fresh) writeFeedToStore(feedKey, fresh, params.page ?? 1)
      },
    },
  )

  if (data) writeFeedToStore(feedKey, data, params.page ?? 1)
}

function writeFeedToStore(
  feedKey: string,
  resp: CommunityFeedResponse,
  page: number,
): void {
  const { posts, authors } = normalizePostList(resp.posts)

  useUserStore.getState().upsertUsers(authors)
  usePostStore.getState().upsertPosts(posts)

  const postIds = posts.map((p) => p.id)
  if (page === 1) {
    useFeedStore.getState().setFeed(feedKey, {
      postIds,
      total: resp.total,
      page,
      hasMore: resp.hasMore,
      staleAt: Date.now() + FEED_SWR.staleTime,
    })
  } else {
    useFeedStore.getState().appendFeed(feedKey, postIds, page, resp.hasMore)
  }
}
